import { useEffect, useRef } from "react";

export default function AmbientParticles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const root = document.documentElement;
    let width = 0, height = 0, frame, last = 0, elapsed = 0;
    let particleRgb = "139, 160, 188";
    const updatePalette = () => {
      particleRgb = getComputedStyle(root).getPropertyValue("--particle-rgb").trim() || particleRgb;
    };
    updatePalette();
    // Deterministic, sparse distribution. No connecting lines or oversized shapes.
    const points = Array.from({ length: 90 }, (_, i) => ({
      x: ((i * 0.61803398875) % 1), y: ((i * 0.41421356237) % 1),
      radius: 0.6 + (i % 4) * 0.25, speed: 4 + (i % 5), phase: i * 1.7,
    }));
    const draw = (time) => {
      const dt = last ? Math.min((time - last) / 1000, 0.05) : 0;
      last = time;
      if (!reduced.matches) elapsed += dt;
      ctx.clearRect(0, 0, width, height);
      const count = width < 650 ? 38 : points.length;
      for (const p of points.slice(0, count)) {
        const x = (p.x * width + Math.sin(elapsed * .12 + p.phase) * 18 + width) % width;
        const y = ((p.y * height - elapsed * p.speed) % height + height) % height;
        ctx.fillStyle = `rgba(${particleRgb},${.12 + (1 + Math.sin(elapsed * .35 + p.phase)) * .08})`;
        ctx.beginPath();
        ctx.arc(x, y, p.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!document.hidden && !reduced.matches) frame = requestAnimationFrame(draw);
    };
    const restart = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (!document.hidden) frame = requestAnimationFrame(draw);
    };
    const resize = new ResizeObserver(([entry]) => {
      width = entry.contentRect.width;
      height = entry.contentRect.height;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      restart();
    });
    resize.observe(canvas);
    const themeObserver = new MutationObserver(() => {
      updatePalette();
      restart();
    });
    themeObserver.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    reduced.addEventListener("change", restart);
    document.addEventListener("visibilitychange", restart);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      themeObserver.disconnect();
      reduced.removeEventListener("change", restart);
      document.removeEventListener("visibilitychange", restart);
    };
  }, []);
  return <canvas ref={ref} className="ambient-particles" aria-hidden="true" />;
}
